/**
 * Backup local durable des projets synoptiques via IndexedDB.
 *
 * localStorage (≈5 Mo) est insuffisant pour les gros projets : IndexedDB
 * accepte des centaines de Mo. Le backup est écrit à chaque tentative de
 * sauvegarde, y compris en cas d'échec cloud → les modifications ne sont
 * jamais perdues côté navigateur.
 */

const DB_NAME = 'visualgrid'
const DB_VERSION = 1
const STORE_NAME = 'synoptic-backups'

export interface SynopticLocalBackup {
  projectId: string
  data: unknown
  savedAt: number
}

let dbPromise: Promise<IDBDatabase> | null = null

function getDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'projectId' })
        }
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }
  return dbPromise
}

/** Écrit/écrase le backup local d'un projet. Ne jette jamais (best-effort). */
export async function saveSynopticLocalBackup(projectId: string, data: unknown): Promise<void> {
  try {
    const db = await getDb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).put({ projectId, data, savedAt: Date.now() })
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  } catch {
    // Backup best-effort : ne jamais casser le flux de sauvegarde
  }
}

/** Lit le backup local d'un projet (null si absent ou IndexedDB indisponible) */
export async function getSynopticLocalBackup(projectId: string): Promise<SynopticLocalBackup | null> {
  try {
    const db = await getDb()
    return await new Promise<SynopticLocalBackup | null>((resolve, reject) => {
      const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(projectId)
      request.onsuccess = () => resolve((request.result as SynopticLocalBackup | undefined) ?? null)
      request.onerror = () => reject(request.error)
    })
  } catch {
    return null
  }
}

/** Supprime le backup local d'un projet. Ne jette jamais (best-effort). */
export async function deleteSynopticLocalBackup(projectId: string): Promise<void> {
  try {
    const db = await getDb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).delete(projectId)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  } catch {
    // Best-effort
  }
}
